import React, { ReactNode, useContext, useMemo } from 'react';

import DataFormat from '../../format/DataFormat';
import resolveKey from '../../../utils/components/_resolveKey';
import resolveStringValue from '../../../utils/components/_resolveStringValue';

import ToggleButton, { ToggleButtonProps } from '../ToggleButton';
import { StyledToggleButtonListItemWrapper, StyledToggleButtonList } from './ToggleButtonList.styles';
import RepeaterContext from '../_RepeaterContext';

export interface ToggleButtonListOption<Value> {
  label?: ReactNode;
  key?: string | string[];
  disabled?: boolean;
  value: Value;
}

export interface ToggleButtonListProps<Value, OptionValue = Value> extends Omit<ToggleButtonProps, 'value'> {
  /** The name of the list. */
  name?: string;
  /** List of options in the list. */
  options?: ToggleButtonListOption<Value>[];
  /** Defines the current value of the `ToggleButtonList`.  */
  value?: Value;
  /** Makes the `ToggleButtonList` width 100% */
  fullWidth?: boolean;
    /** Callback when the value changes. */
  onValueChange?: (value: OptionValue | Value) => void;
}

const ToggleButtonListDefaultProps: Partial<ToggleButtonListProps<any>> = {
  options: [],
  fullWidth: false,
};

/**
 * @mode developer
 */
const ToggleButtonList = <Value extends Object | string | boolean | number, >({
  options, value, name, isValid, isTouched, isSwitch, isChips, textOnly, fullWidth, disabled, ...props
}: ToggleButtonListProps<Value>) => {
  const isSelected = useMemo(() => typeof value !== 'undefined' && value !== '', [value]);

  const { repeaterId, repeaterItemId } = useContext(RepeaterContext);

  if (textOnly) {
    const selectedOption = options.find((option) => option.value === value);
    return <DataFormat>{selectedOption?.label}</DataFormat>;
  }

  return (
    <StyledToggleButtonList
      data-testid="ToggleButtonList"
      data-test-name={name}
      isSwitch={isSwitch}
      isChips={isChips}
      fullWidth={fullWidth}
      id={props.id}
    >
      <StyledToggleButtonListItemWrapper
        fullWidth={fullWidth}
      >
        {options.map(({ label, value: optionValue, key, disabled: optionDisabled }, index) => {
          const propsToggle = props.id ? { ...props, id: `${props.id}Toggle${index + 1}` } : props;
          return (
            <ToggleButton
              {...propsToggle}
              isSwitch={isSwitch}
              isChips={isChips}
              isValid={isValid}
              isTouched={isTouched}
              isSelected={isSelected}
              name={repeaterId ? `${name}-${repeaterId}-${repeaterItemId}` : name}
              data-index={index}
              key={resolveKey(optionValue, key)}
              label={label}
              value={resolveStringValue(optionValue, key)}
              checked={resolveStringValue(value, key) === resolveStringValue(optionValue, key)}
              disabled={disabled || optionDisabled}
              fullWidth={fullWidth}
            />
          );
        })}
      </StyledToggleButtonListItemWrapper>
    </StyledToggleButtonList>
  );
};

ToggleButtonList.defaultProps = ToggleButtonListDefaultProps;

export default ToggleButtonList;
